import { decrypt, encrypt } from "../storage/cipher";
import { LEGACY_LOCAL_UPDATED_AT, LEGACY_SERVER_UPDATED_AT, PLAYLIST_SCHEMA_VERSION } from "./constants";
import { storageError } from "./errors";
import { computeFingerprint } from "./identity";
import { applyTombstones, mergeIntoTarget } from "./merge";
import { normalizeSelection } from "./records";

// The key the app kept the register_device list under before this service existed (plain JSON).
// It is only read, once, and never written or deleted.
export const LEGACY_STORAGE_KEY = "playlists";

const emptyReport = () => ({
    found: 0,
    imported: 0,
    skippedTombstoned: 0,
    skippedDuplicate: 0,
    skippedInvalid: 0,
});

const parseTime = (value) => {
    if (typeof value !== "string") return 0;
    const t = Date.parse(value);
    return isFinite(t) ? t : 0;
};

const sameRecord = (a, b) => !!a && JSON.stringify(a) === JSON.stringify(b);

/**
 * The two one-time imports into the store:
 *  - the legacy `playlists` key (marker `legacyImportedAt`);
 *  - the list register_device returns (marker `PlaylistGeted`).
 * Both keep the backend's id (uid `legacy-<id>`), so `playlistData-<id>` keeps working. Markers are set only
 * after the data is written and read back, in a second commit: a crash in between leaves data without a
 * marker (the import runs again, harmlessly, every step is keyed by uid), never a marker without data.
 *
 * `channel`: { commit(change), readBack() } of the store being written into.
 * `state`: { playlists, tombstones, meta } as the store holds it now.
 */
export class PlaylistMigrationService {

    constructor(deps) {
        this.deps = deps;
    }

    // A read problem never throws: the app keeps running and the import is tried again next start.
    // status: ok | absent | corrupt (final) | error (couldn't read the key at all, retry)
    readLegacyLocal() {

        const result = { records: [], found: 0, skipped: 0, status: "absent" };
        const storage = this.deps.legacyStorage;

        if (!storage) return result;

        let raw;

        try {
            raw = storage.getItem(LEGACY_STORAGE_KEY);
        } catch (e) {
            result.status = "error";
            return result;
        }

        if (raw === null || raw === "") return result;

        let list;

        try {
            list = JSON.parse(raw);
        } catch (e) {
            result.status = "corrupt";
            return result;
        }

        if (!Array.isArray(list)) {
            result.status = "corrupt";
            return result;
        }

        result.status = "ok";

        list.forEach((item) => {
            result.found++;
            const url = item && typeof item.url === "string" ? item.url : "";
            // this app kept the URL readable; an encrypted one (other builds) is accepted too
            const plain = decrypt(url) || (/^https?:\/\//i.test(url) ? url : "");
            const record = this.toRecord(item, plain, "legacy-local", LEGACY_LOCAL_UPDATED_AT);
            if (record) result.records.push(record);
            else result.skipped++;
        });

        return result;

    }

    convertServerList(list) {

        const result = { records: [], found: 0, skipped: 0 };

        list.forEach((item) => {
            result.found++;
            const plain = item && typeof item.url === "string" ? item.url : "";
            const record = this.toRecord(item, plain, "legacy-server", LEGACY_SERVER_UPDATED_AT);
            if (record) result.records.push(record);
            else result.skipped++;
        });

        return result;

    }

    async migrateLegacy(channel, state) {

        const report = emptyReport();

        const applied = applyTombstones(state.playlists, state.tombstones);

        const legacy = this.readLegacyLocal();
        report.found = legacy.found;
        report.skippedInvalid = legacy.skipped;

        const result = mergeIntoTarget(applied.survivors, legacy.records, state.tombstones, { allowIncomingDuplicates: true });
        report.imported = result.stats.added + result.stats.updated;
        report.skippedTombstoned = result.stats.skippedTombstoned;
        report.skippedDuplicate = result.stats.skippedDuplicate;

        const metaPatch = {};
        // `error` means the key couldn't be read; leave the marker unset so the next start tries again
        if (legacy.status !== "error") metaPatch.legacyImportedAt = this.deps.now();

        const next = await this.writeAndVerify(channel, state, normalizeSelection(result.records), state.tombstones, applied.removed, metaPatch);

        return { state: next, report };

    }

    async importServerList(channel, state, list) {

        const report = emptyReport();

        const converted = this.convertServerList(list);
        report.found = converted.found;
        report.skippedInvalid = converted.skipped;

        const result = mergeIntoTarget(state.playlists, converted.records, state.tombstones, { allowIncomingDuplicates: true });
        report.imported = result.stats.added + result.stats.updated;
        report.skippedTombstoned = result.stats.skippedTombstoned;
        report.skippedDuplicate = result.stats.skippedDuplicate;

        const next = await this.writeAndVerify(channel, state, normalizeSelection(result.records), state.tombstones, [], { PlaylistGeted: true });

        return { state: next, report };

    }

    toRecord(item, plainUrl, origin, updatedAt) {

        if (!item || typeof item !== "object") return null;
        // id -1 is the built-in demo playlist, which is never stored
        if (typeof item.id !== "number" || !isFinite(item.id) || Math.floor(item.id) !== item.id || item.id <= 0) return null;
        if (!plainUrl) return null;

        const urlEnc = encrypt(plainUrl);
        if (!urlEnc) return null;

        let fingerprint;

        try {
            fingerprint = computeFingerprint(plainUrl);
        } catch (e) {
            return null;
        }

        const record = {
            uid: "legacy-" + item.id,
            id: item.id,
            name: typeof item.name === "string" ? item.name : "",
            urlEnc,
            fingerprint,
            schemaVersion: PLAYLIST_SCHEMA_VERSION,
            createdAt: parseTime(item.createdAt),
            updatedAt,
            origin,
        };

        if (typeof item.epg_url === "string" && item.epg_url) {
            const epgEnc = encrypt(item.epg_url);
            if (epgEnc) record.epgUrlEnc = epgEnc;
        }

        const shownUpdatedAt = parseTime(item.updatedAt);
        if (shownUpdatedAt > 0) record.importedUpdatedAt = shownUpdatedAt;

        if (typeof item.is_selected === "boolean") record.is_selected = item.is_selected;
        if (typeof item.status === "string") record.status = item.status;
        if (typeof item.channels_count === "number") record.channels_count = item.channels_count;
        if (typeof item.movies_count === "number") record.movies_count = item.movies_count;
        if (typeof item.series_count === "number") record.series_count = item.series_count;
        if (typeof item.expired_date === "string") record.expired_date = item.expired_date;

        return record;

    }

    // Writes only what changed, reads it back, and only then sets the markers in a second commit.
    async writeAndVerify(channel, state, records, tombstones, removed, metaPatch) {

        const before = {};
        state.playlists.forEach((r) => { before[r.uid] = r; });

        const knownTombstones = {};
        state.tombstones.forEach((t) => { knownTombstones[t.uid] = t.deletedAt; });

        const put = records.filter((r) => !sameRecord(before[r.uid], r));
        const putTombstones = tombstones.filter((t) => knownTombstones[t.uid] !== t.deletedAt);

        if (put.length || removed.length || putTombstones.length) {

            await channel.commit({ put, remove: removed, putTombstones });

            const snapshot = await channel.readBack();
            const stored = {};
            snapshot.playlists.forEach((r) => { stored[r.uid] = r; });

            const complete = records.every((r) => {
                const s = stored[r.uid];
                return !!s && s.fingerprint === r.fingerprint && s.updatedAt === r.updatedAt;
            }) && removed.every((uid) => !stored[uid]);

            if (!complete) throw storageError("STORAGE_ERROR", "verify_failed");

        }

        const meta = { ...state.meta, ...metaPatch };

        if (Object.keys(metaPatch).length) await channel.commit({ meta });

        return { playlists: records, tombstones, meta };

    }

}
